/**
 * useTypewriter.js
 *
 * Character-level streaming drain — buffers incoming SSE text and drains
 * at ~4 characters per requestAnimationFrame (~240 chars/sec at 60fps).
 *
 * This produces the smooth, continuous text-growth that ChatGPT and Gemini
 * use, rather than the jerkier word-by-word tick approach.
 *
 * Analogy: Old approach = flipping a calendar one page per second (visible
 * jumps). New approach = water filling a glass (continuous, smooth).
 *
 * API:
 *   append(text)  — add new SSE chunk to the pending buffer
 *   flush()       — instantly reveal everything (call on streaming_complete)
 *   reset()       — clear buffer + display (call on new generation start)
 *   displayText   — current rendered string (drives React state)
 */

import { useState, useRef, useCallback, useEffect } from 'react';

// Characters to reveal per animation frame (~180 chars/sec at 60fps).
// Slightly slower than before for smoother visual flow.
const CHARS_PER_FRAME = 3;
// Fast-drain threshold: if the buffer builds up (slow render / large chunks),
// drain faster to avoid visible lag without a jarring jump.
const CHARS_PER_FRAME_FAST = 8;
const FAST_DRAIN_THRESHOLD = 50;

function useTypewriter() {
  const [displayText, setDisplayText] = useState('');

  // Characters waiting to be displayed
  const pendingRef = useRef('');
  // Complete raw buffer (used by flush to instantly reveal everything)
  const fullBufferRef = useRef('');
  // rAF handle
  const rafRef = useRef(null);
  // Whether the drain loop is scheduled
  const drainingRef = useRef(false);

  // ── Drain loop: reveals chars per frame, fast-drains if buffer builds ─────
  const drain = useCallback(() => {
    if (pendingRef.current.length === 0) {
      drainingRef.current = false;
      rafRef.current = null;
      return;
    }

    // Use fast-drain rate when buffer is large (prevents visible lag from irregular SSE chunks)
    const rate =
      pendingRef.current.length > FAST_DRAIN_THRESHOLD ? CHARS_PER_FRAME_FAST : CHARS_PER_FRAME;
    const chunk = pendingRef.current.slice(0, rate);
    pendingRef.current = pendingRef.current.slice(rate);
    setDisplayText(prev => prev + chunk);

    rafRef.current = requestAnimationFrame(drain);
  }, []);

  const ensureDraining = useCallback(() => {
    if (!drainingRef.current) {
      drainingRef.current = true;
      rafRef.current = requestAnimationFrame(drain);
    }
  }, [drain]);

  // ── append: queue new text ─────────────────────────────────────────────────
  const append = useCallback(
    text => {
      if (!text) return;
      fullBufferRef.current += text;
      pendingRef.current += text;
      ensureDraining();
    },
    [ensureDraining]
  );

  // ── flush: instantly reveal all pending text ───────────────────────────────
  const flush = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    drainingRef.current = false;
    pendingRef.current = '';
    const full = fullBufferRef.current;
    if (full) setDisplayText(full);
  }, []);

  // ── reset: clear everything for a new generation ──────────────────────────
  const reset = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    drainingRef.current = false;
    pendingRef.current = '';
    fullBufferRef.current = '';
    setDisplayText('');
  }, []);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return { displayText, append, flush, reset };
}

export default useTypewriter;
